<template>
  <div class="panel">
    <div class="body">
      <div v-if="entity" class="card">
        <div class="summary">
          <div class="summary-layout">
            <div class="summary-metrics">
              <span>H: {{ holdersText }}</span>
              <span>A: {{ amountText }}</span>
              <span>L: {{ liquidityText }}</span>
              <span>T: {{ tradesText }}</span>
            </div>

            <Media
              :mint="entity.mint || ''"
              :name="local.name || entity.name || ''"
              :image="local.image"
              :website="local.website"
              :twitter="local.twitter"
              :telegram="local.telegram"
              :is-our-coin="isOurCoin"
              @actions="$emit('actions')"
            />

            <div class="summary-text">
              <Description
                :entity="entity"
                :local="local"
                :can-edit="canEdit"
                :editing-description="editingDescription"
                @start="startEditDescription"
                @stop="stopEditDescription"
                @desc-input="onDescriptionInput"
              />

              <LinksEdit
                v-if="canEdit"
                :local="local"
                @dirty="markDirty"
              />

              <div v-if="entity.status" class="tags">
                <span class="chip">{{ entity.status }}</span>
              </div>

              <Actions
                :entity="entity"
                :can-edit="canEdit"
                :is-dirty="isDirty"
                :saving="saving"
                :is-our-coin="isOurCoin"
                @save="saveChanges"
                @reset="resetChanges"
              />
            </div>
          </div>
        </div>

        <Chart :entity="entity" :trades="trades" />
        <Positions :entity="entity" :position-pnl="positionPnl" />
      </div>

      <div v-else class="placeholder">No coin selected</div>
    </div>

  </div>
</template>

<script setup>
import { useCoinDraft } from './useDraft'
import { computed } from 'vue'
import { formatAmountHuman, formatSol } from '@/composables/format'

import Media from './media.vue'
import Chart from './chart.vue'
import Description from './description.vue'
import LinksEdit from './links_edit.vue'
import Actions from './actions.vue'
import Positions from './positions.vue'

const props = defineProps({
  entity: { type: Object, default: null },
  trades: { type: Array, default: () => [] },
  positionPnl: { type: Object, default: null },
})
defineEmits(['actions'])

const holdersText = computed(() => compactCount(props.entity?.holders_count || props.entity?.holdersCount || 0))
const amountText = computed(() => formatAmountHuman(props.entity?.amount || 0))
const liquidityText = computed(() => formatSol(props.entity?.lamports || 0))
const tradesText = computed(() => compactCount(props.entity?.trades || 0))

const {
  local,
  saving,
  isDirty,
  editingDescription,

  isOurCoin,
  canEdit,

  markDirty,
  startEditDescription,
  stopEditDescription,
  onDescriptionInput,

  saveChanges,
  resetChanges,
} = useCoinDraft(props)

function compactCount(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return String(Math.trunc(n))
}
</script>

<style scoped>
.panel{
  background:#111;
  border:1px solid #222;
  border-radius:8px;
  overflow:hidden;
}

.body{
  padding:8px;
  overflow-y:auto;
}

.card{
  display:grid;
  grid-template-columns:minmax(0, 1fr);
  gap:8px;
  padding:8px;
  align-items:stretch;
}

.summary{
  min-width:0;
  display:block;
}

.summary-layout{
  min-width:0;
  display:grid;
  grid-template-columns:112px minmax(0, 1fr);
  grid-template-rows:auto auto minmax(0, 1fr);
  gap:8px 14px;
  align-items:start;
}

.summary-metrics{
  grid-column:2;
  grid-row:1;
  min-width:0;
  display:flex;
  align-items:center;
  gap:6px;
  overflow:hidden;
  color:#8fa1b5;
  font-size:11px;
  font-weight:500;
  line-height:1;
  font-variant-numeric:tabular-nums;
  white-space:nowrap;
}
.summary-metrics span{
  flex:0 0 auto;
  min-width:0;
}

.summary-text{
  grid-column:2;
  grid-row:3;
  min-width:0;
  display:grid;
  gap:8px;
}

.tags{ margin-top:4px; }
.chip{
  padding:4px 10px;
  border-radius:999px;
  border:1px solid #4b5563;
  background:#111827;
  color:#aaa;
  font-size:10px;
  letter-spacing:0.06em;
  text-transform:uppercase;
}

.placeholder{
  padding:60px 16px;
  text-align:center;
  color:#555;
  font-size:14px;
}


@media (max-width: 640px) {
  .panel{
    height:100%;
    min-height:0;
  }
  .header{
    display:none;
  }
  .body{
    height:100%;
    min-height:0;
    overflow-y:auto;
    padding:6px;
  }
  .card{
    min-height:0;
    display:grid;
    grid-template-columns:1fr;
    grid-template-rows:auto auto auto;
    gap:8px;
    padding:0;
  }
  .summary{
    gap:6px;
  }
  .summary-layout{
    display:grid;
    grid-template-columns:88px minmax(0, 1fr);
    grid-template-rows:auto auto minmax(0, 1fr);
    gap:7px 10px;
  }
  .summary-metrics{
    gap:5px;
    font-size:10px;
  }
  .summary :deep(textarea),
  .summary :deep(.links-edit){
    display:none;
  }
}
</style>
